import { getCurrencySymbol } from '@angular/common';
import { InvoiceRegister } from '../../models/invoice/invoice-register.model';
import { InvoiceState } from '../../models/invoice/invoice-state.model';
import { BackDeliveries, BackDelivery, Invoice, OrderingAdditionalService } from '../../models/invoice/invoice.model';
import { GetTemplateInfoResponseData } from '../../models/template.model';
import { GetDocumentData } from '../../services/invoices/invoice-api.models';
import { dateTimeToString, dateToString, todayEqualOrGreaterThan } from './date.util';
import { IInternationalInvoice } from '../../models/international-invoice';
import { ECategoryOfWarehouse } from '@models';

export function convertInvoiceDate(invoice: Invoice): Date {
  return new Date(JSON.parse(JSON.stringify(invoice.createTime.getTime())));
}

export function convertRegisterDate(register: InvoiceRegister): Date {
  return new Date(JSON.parse(JSON.stringify(register.dateTime.getTime())));
}

export function checkInRegisters(invoices: Invoice[] | IInternationalInvoice[]): number {
  for (let i = 0; i < invoices.length; i++) {
    if (!!invoices[i].ScanSheetRef) {
      return i;
    }
  }
  return -1;
}

export function handleFirstDayStorage(invoice: Invoice): Invoice {
  const isTodayGreaterThanFirstDayStorage = (i: Invoice): boolean => {
    if (!i) {
      return false;
    }
    try {
      const dateFirstDayStorage = new Date(i.dateFirstDayStorage);
      return todayEqualOrGreaterThan(dateFirstDayStorage);
    } catch (e) {
      return false;
    }
  };

  const isReceived =
    invoice &&
    invoice.statusCode &&
    !!invoice.dateFirstDayStorage &&
    (Number(invoice.statusCode) === 7 || Number(invoice.statusCode) === 8);

  if (isReceived && isTodayGreaterThanFirstDayStorage(invoice)) {
    const actualDeliveryDate = new Date(invoice.actualDeliveryDate);
    const categoryOfWarehouse = invoice.authenticData?.CategoryOfWarehouse;

    const newInvoice = {
      ...(invoice as Invoice),
      state: {
        ...(invoice.state as InvoiceState),
        id: '6',
        name: `${mapStateName(categoryOfWarehouse)} ${dateTimeToString(actualDeliveryDate)}<br>Розпочате платне зберігання`,
        shortName: 'Платне зберігання',
      } as InvoiceState,
    } as Invoice;
    return newInvoice;
  }

  return invoice;
}

export function mapStateName(categoryOfWarehouse: ECategoryOfWarehouse) {
  switch (categoryOfWarehouse) {
    case ECategoryOfWarehouse.Postomat:
      return 'Прибув у поштомат';
    case ECategoryOfWarehouse.Store:
      return 'Прибув у пункт';
    default:
      return 'Прибув у відділення';
  }
}

export function handleReturnCargoDate(invoice: Invoice): Invoice {
  const parcelDelivered = Number(invoice.statusCode) === 7 || Number(invoice.statusCode) === 8;

  if ((!!invoice.dateReturnCargo || parcelDelivered) && invoice.state && Number(invoice.state.id) !== 6) {
    const dateReturn = new Date(invoice.dateReturnCargo);
    dateReturn.setDate(dateReturn.getDate() + 1);
    const actualDeliveryDate = new Date(invoice.actualDeliveryDate);
    const categoryOfWarehouse = invoice.authenticData?.CategoryOfWarehouse;

    const categoryOfWarehouseStateName = `${mapStateName(categoryOfWarehouse)}${
      invoice.actualDeliveryDate ? ` ${dateTimeToString(actualDeliveryDate)}` : ''
    }`;
    const autoReturnStateName = invoice.dateReturnCargo ? `<br>Автоповернення ${dateToString(dateReturn)}` : '';

    const newInvoice = {
      ...(invoice as Invoice),
      state: {
        ...(invoice.state as InvoiceState),
        name: categoryOfWarehouseStateName + autoReturnStateName,
      } as InvoiceState,
    } as Invoice;
    return newInvoice;
  }
  return invoice;
}

export type CargoTypeRef = 'Money' | 'Documents' | 'SignedDocuments' | 'Other';

export const findBackDelivery = (
  backwardDeliveryData: any[],
  cargoTypeRef: CargoTypeRef,
) => {
  return backwardDeliveryData
    ? Object.values(backwardDeliveryData).find(
      (delivery) => delivery.CargoTypeRef === cargoTypeRef,
    )
    : null;
};

export const parseBackDeliveriesFromInvoice = (
  invoice: Invoice | GetTemplateInfoResponseData | GetDocumentData,
) => {
  const backwardDeliveryData: { [key: string]: BackDelivery } =
    invoice.BackwardDeliveryData;
  const backDeliveries: BackDeliveries = {};
  Object.values(backwardDeliveryData).map((delivery) => {
    if (delivery.CargoTypeRef === 'Money') {
      backDeliveries.Money = delivery;
    }
    if (delivery.CargoTypeRef === 'Documents' && !delivery.Services) {
      backDeliveries.Documents = delivery;
    }
    if (delivery.CargoTypeRef === 'Documents' && delivery.Services) {
      backDeliveries.DifferentDocuments = delivery;
    }
    if (delivery.CargoTypeRef === 'SignedDocuments') {
      backDeliveries.SignedDocuments = delivery;
    }
    if (delivery.CargoTypeRef === 'Other') {
      backDeliveries.Other = delivery;
    }
  });
  return backDeliveries;
};

export const getBackDeliveriesDescriptionForTooltip = (
  invoice: Invoice,
): string => {
  return Object.keys(invoice.backDeliveries)
    .map((key) => {
      const delivery = invoice.backDeliveries[key];
      if (delivery.CargoTypeRef === 'Money') {
        return '';
      }
      if (delivery.CargoTypeRef === 'SignedDocuments') {
        return 'Підписані документи';
      }
      if (delivery.CargoTypeRef === 'Documents') {
        return delivery.Services && !(delivery.Services instanceof Array)
          ? 'Підтипи документів'
          : 'Документи';
      }
      return delivery.RedeliveryString;
    })
    .filter((val) => val !== '')
    .join(', ');
};

export const getBackDeliveryDescriptionForForm = (
  backDelivery: BackDelivery,
): string => {
  if (backDelivery.Services && Object.keys(backDelivery.Services).length) {
    return 'Підтипи документів';
  }
  if (
    backDelivery.CargoTypeRef === 'Documents' &&
    backDelivery.RedeliveryString
  ) {
    return backDelivery.RedeliveryString;
  }
  if (backDelivery.RedeliveryString) {
    if (
      isNaN(Number(backDelivery.RedeliveryString)) ||
      backDelivery.RedeliveryString === ' '
    ) {
      return backDelivery.RedeliveryString;
    } else {
      return `${backDelivery.RedeliveryString} грн`;
    }
  }
  return '0';
};

export function checkCanEdit(additionalServices: OrderingAdditionalService[], orderType: string): boolean {
  if (!additionalServices?.length) {
    return false;
  }

  return additionalServices.some(
    (s) => s.OrderType === orderType && (s.Status === 'Received' || s.Status === 'StoreInProcessing'),
  );
}

export function getOrderRef(additionalServices: OrderingAdditionalService[], orderType: string): string | undefined {
  if (!additionalServices?.length) {
    return;
  }

  return additionalServices.find(s => s.OrderType === orderType && (s.Status === 'Received' || s.Status === 'StoreInProcessing'))?.Ref;
}

export function toTwoDecimals(value: number): number {
  if (isNaN(value)) {
    return value;
  }
  return Math.round(value * 100) / 100;
}

export function roundToPrecision(value: number | string, precision: number): string {
  if (typeof value === 'string') {
    value = parseFloat(value);
  }

  const factor = Math.pow(10, precision);

  // Add a small value (epsilon) to avoid floating-point rounding issues
  const epsilon = 1e-10;

  // Multiply, round, then divide by factor to shift the decimal place
  const roundedNumber = Math.round((value + epsilon) * factor) / factor;

  // Use toFixed to ensure the precision and retain trailing zeros
  return roundedNumber.toFixed(precision);
}

export function formatCostForInternationalInvoice(cost: number, currencyCode: string): string {
  if (!currencyCode) {
    return cost.toString();
  }

  if (currencyCode === 'UAH') {
    return cost + ' грн';
  } else {
    return cost + ' ' + getCurrencySymbol(currencyCode, 'narrow');
  }
}

export function formatCostForInvoicesReport(value: string | object): string {
  if (typeof value === 'object') {
    return '-';
  }

  return value || '-';
}

export function formatFieldDataForInvoicesReport(
  value: unknown,
  fallback: string | number = '',
): string | number {
  if (value === null || value === undefined || typeof value === 'object') {
    return fallback;
  }

  if (typeof fallback === 'number' || typeof value === 'number') {
    const numericValue = Number(value);
    return isNaN(numericValue) ? fallback : numericValue;
  }

  if (typeof value === 'string') {
    return value.trim() || fallback;
  }

  return fallback;
}

export function getInvoiceAnnouncedPrice({
  announcedPrice,
  isInternational,
  Cost,
  InvoiceCurrencyCode,
}: Invoice): string {
  const price = (announcedPrice ?? Cost) || 0;
  const formattedPrice = toTwoDecimals(price);

  return isInternational
    ? formatCostForInternationalInvoice(formattedPrice, InvoiceCurrencyCode)
    : `${formattedPrice} грн`;
}
